/**
 * Terms of Service Page
 * ======================
 * Public page - accessible without authentication
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, Ban, CreditCard, Gavel, RefreshCw, Mail } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: FileText,
      title: '1. Acceptance of Terms',
      content: `By accessing or using NearU, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this application.

These Terms constitute a legally binding agreement between you and NearU Inc. regarding your use of the Service.`
    },
    {
      icon: Scale,
      title: '2. Eligibility',
      content: `To use NearU, you must:

• Be at least 18 years of age
• Be legally permitted to use the service in your jurisdiction
• Not be prohibited from using the service under any applicable laws
• Not have been previously banned or removed from NearU
• Complete the registration process truthfully and accurately

By using NearU, you represent and warrant that you meet all eligibility requirements.`
    },
    {
      icon: AlertTriangle,
      title: '3. User Conduct',
      content: `You agree not to:

• Provide false or misleading information
• Use the service for any illegal purpose
• Harass, abuse, or harm other users
• Impersonate any person or entity
• Post content that is defamatory, obscene, or offensive
• Attempt to gain unauthorized access to accounts or systems
• Use automated systems or bots
• Engage in any activity that interferes with the service
• Solicit money from other users
• Share content that violates intellectual property rights`
    },
    {
      icon: Ban,
      title: '4. Prohibited Content',
      content: `The following content is strictly prohibited:

• Nudity or sexually explicit material
• Violence or threats of violence
• Hate speech or discrimination
• Illegal activities or substances
• Spam or commercial solicitation
• Private information of others
• Content involving minors
• Fraudulent or deceptive content

NearU reserves the right to remove any content and suspend accounts that violate these guidelines.`
    },
    {
      icon: CreditCard,
      title: '5. Payments and Subscriptions',
      content: `Premium Features:
• Certain features require a paid subscription
• Prices are displayed before purchase
• Subscriptions auto-renew unless cancelled

Refund Policy:
• Refund requests are handled on a case-by-case basis
• Digital services are generally non-refundable
• Contact support within 14 days for billing issues

Payment Processing:
• Payments are processed securely through third-party providers
• You are responsible for all charges incurred on your account`
    },
    {
      icon: Gavel,
      title: '6. Intellectual Property',
      content: `NearU Content:
• The NearU name, logo, and all related marks are trademarks
• All content, features, and functionality are owned by NearU Inc.
• You may not copy, modify, or distribute our intellectual property

Your Content:
• You retain ownership of content you post
• You grant NearU a license to use, display, and distribute your content
• You represent that you have the right to share any content you post`
    },
    {
      icon: RefreshCw,
      title: '7. Account Termination',
      content: `You may terminate your account at any time through the app settings.

NearU may terminate or suspend your account if you:
• Violate these Terms of Service
• Engage in fraudulent or illegal activity
• Provide false information
• Harm other users or the community

Upon termination:
• Your right to use the service ceases immediately
• We may delete your data in accordance with our Privacy Policy
• Certain provisions of these Terms survive termination`
    },
    {
      icon: Scale,
      title: '8. Limitation of Liability',
      content: `To the maximum extent permitted by law:

• NearU is provided "as is" without warranties
• We do not guarantee matches, connections, or outcomes
• We are not responsible for user conduct or content
• Our liability is limited to the amount you paid for the service
• We are not liable for indirect, incidental, or consequential damages

You use NearU at your own risk and are solely responsible for your interactions with other users.`
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)',
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px 20px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <NearULogo size="small" showText={true} />
      </div>

      <div style={{ padding: '24px 20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          fontWeight: '600',
          color: '#E6D7C3',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Terms of Service
        </h1>
        
        <p style={{
          color: '#A57C33',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          Effective Date: December 2025
        </p>

        {/* Introduction */}
        <div style={{
          background: 'rgba(215, 181, 109, 0.08)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid rgba(165, 124, 51, 0.2)'
        }}>
          <p style={{
            color: '#E6D7C3',
            fontSize: '15px',
            lineHeight: '1.7',
            margin: 0
          }}>
            Welcome to NearU. These Terms of Service ("Terms") govern your access to and use of the 
            NearU application and services. By creating an account or using our services, you agree 
            to these Terms. Please read them carefully.
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <div
              key={index}
              style={{
                background: 'rgba(26, 15, 10, 0.6)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '16px',
                border: '1px solid rgba(165, 124, 51, 0.15)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={20} color="#D7B56D" />
                </div>
                <h2 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#E6D7C3',
                  margin: 0
                }}>
                  {section.title}
                </h2>
              </div>
              <p style={{
                color: '#A57C33',
                fontSize: '14px',
                lineHeight: '1.8',
                margin: 0,
                whiteSpace: 'pre-line'
              }}>
                {section.content}
              </p>
            </div>
          );
        })}

        {/* Contact */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.08) 100%)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          border: '1px solid rgba(215, 181, 109, 0.3)'
        }}>
          <Mail size={32} color="#D7B56D" style={{ marginBottom: '12px' }} />
          <h3 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '18px',
            color: '#E6D7C3',
            marginBottom: '8px'
          }}>
            Questions About These Terms?
          </h3>
          <p style={{
            color: '#A57C33',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Contact our Legal Team
          </p>
          <a
            href="mailto:legal@nearu.com"
            style={{
              color: '#D7B56D',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            legal@nearu.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
